// src/components/Room/RoomLockToggleButton.tsx
import React from "react";
import { ActionIcon, Tooltip, Text } from "@mantine/core";
import { FaLock, FaLockOpen } from "react-icons/fa6";
import { useAppSelector } from "../../app/hooks";
import { selectHttpSingleRoomFull } from "../../Room/roomSelectors";
import { selectProjectNo } from "../../Room/RoomSlice";
import { usePutMultiplacerRoomLockMutation } from "../../../openapi";

export const RoomLockToggleButton: React.FC = () => {
  const projectNo = useAppSelector(selectProjectNo);
  const singleRoom = useAppSelector(selectHttpSingleRoomFull);
  const selectedSpaceNumber = useAppSelector((s) => s.design.selectedSpaceNumber) as string | null;

  const lockedInfo = singleRoom?.multiplacerCeilingDevicesLocked;
  const isLocked = lockedInfo?.bool === true;

  const [putRoomLock, { isLoading }] = usePutMultiplacerRoomLockMutation();

  const handleToggleLock = async () => {
    if (!selectedSpaceNumber) {
      console.warn("Ingen valgt rom – kan ikke låse/åpne.");
      return;
    }

    try {
      await putRoomLock({
        body: {
          projectNo,
          roomNumber: selectedSpaceNumber,
          lock: !isLocked,
        },
      }).unwrap();
    } catch (error) {
      console.error("Feil ved låsing/åpning av rom", error);
    }
  };

  // Formatér "låst av" + dato
  const lockedByName = lockedInfo?.createdBy?.name;
  const lockedAtRaw = lockedInfo?.lockedAt as string | Date | undefined;

  let lockedAtFormatted: string | null = null;
  if (lockedAtRaw) {
    const d = new Date(lockedAtRaw);
    if (!Number.isNaN(d.getTime())) {
      lockedAtFormatted = d.toLocaleString("nb-NO", {
        dateStyle: "short",
        timeStyle: "short",
      });
    }
  }

  const tooltipContent = isLocked ? (
    <div>
      <Text size="xs" fw={500}>
        Rommet er låst
      </Text>
      {lockedByName && (
        <Text size="xs">
          Låst av <b>{lockedByName}</b>
        </Text>
      )}
      {lockedAtFormatted && (
        <Text size="xs" c="dimmed">
          Låst {lockedAtFormatted}
        </Text>
      )}
      <Text size="xs" mt={4} c="dimmed">
        Klikk for å låse opp og kunne redigere komponenter.
      </Text>
    </div>
  ) : (
    <Text size="xs">Lås rommet slik at komponenter ikke kan endres.</Text>
  );

  return (
    <Tooltip label={tooltipContent} multiline>
      <ActionIcon
        h={30}
        w={30}
        radius="md"
        variant="light"
        color={isLocked ? "red" : "green"}
        onClick={handleToggleLock}
        disabled={isLoading}
      >
        {isLocked ? <FaLock size={14} /> : <FaLockOpen size={14} />}
      </ActionIcon>
    </Tooltip>
  );
};
